import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom hook for scroll-triggered animations
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

const LandingPageLATAM: React.FC = () => {
  const navigate = useNavigate();
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Track ad visit on page load — capture UTM params from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (hasUtm) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/api/ad-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          landing_page: '/latam',
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          gclid: params.get('gclid') || undefined,
          referrer: document.referrer || undefined,
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.visit_id) {
            localStorage.setItem('ad_visit_id', String(data.visit_id));
          }
        })
        .catch(() => {});
    }
  }, []);

  const goLogin = useCallback(() => {
    // Fire Google Ads conversion
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'conversion', {
        send_to: 'AW-18013772297/FiwJCN68lIocEIm00Y1D',
        value: 1.0,
        currency: 'USD',
      });
    }
    navigate('/login');
  }, [navigate]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  }, []);

  // Section animation hooks
  const pain = useInView();
  const features = useInView();
  const stats = useInView();
  const useCases = useInView();
  const cta = useInView();

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Cubemate
              </span>
            </div>
            <button
              onClick={goLogin}
              className="px-5 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25"
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        {/* Background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Copy */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-8">
                Built for People Who Want to Get Hired
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.1] mb-6">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Crack Interviews
                </span>{' '}
                2x Faster
              </h1>

              <p className="text-xl sm:text-2xl text-gray-300 font-medium mb-4">
                From first line of code to first job offer.
              </p>

              <div className="space-y-3 mb-8 text-left max-w-lg mx-auto lg:mx-0">
                {[
                  'Tired of tutorials that don\'t stick?',
                  'AI generates code. You still can\'t explain it.',
                  'Your next job needs skills, not copied snippets.',
                ].map((line, i) => (
                  <p key={i} className="text-gray-400 text-base italic">
                    "{line}"
                  </p>
                ))}
                <p className="text-lg font-semibold text-white pt-2">
                  Cubemate fixes that.
                </p>
              </div>

              <button
                onClick={goLogin}
                className="px-8 py-3.5 text-base font-bold rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25"
              >
                Start Learning Free
              </button>
            </div>

            {/* Right - Product Video */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-purple-500/10">
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-blue-500/30 via-purple-500/30 to-pink-500/30 -z-10 blur-sm" />

                <video
                  ref={videoRef}
                  className="w-full h-full object-cover cursor-pointer"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onClick={toggleMute}
                  title="Cubemate - AI Code Learning Platform Demo"
                >
                  <source src="/cubemate-demo.mp4" type="video/mp4" />
                </video>

                {isMuted && (
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-2.5 bg-black/60 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium hover:bg-black/80 transition-all animate-pulse"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                    Tap to unmute
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sub-headline */}
          <p className="text-center text-gray-400 text-lg mt-16 max-w-2xl mx-auto">
            Understand ANY code in minutes. Write it, run it, explain it.
          </p>
        </div>
      </section>

      {/* ── Pain to Solution ── */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent">
        <div
          ref={pain.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            pain.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              The Problem with{' '}
              <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                Learning Today
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                problem: 'You watch a 4-hour tutorial.',
                solution: 'Cubemate gives you an interactive tutorial you actually run, line by line.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                ),
                gradientFrom: 'from-red-500/20',
                gradientTo: 'to-blue-500/20',
                border: 'border-red-500/20 hover:border-blue-400/40',
              },
              {
                problem: 'ChatGPT writes your code. You can\'t debug it.',
                solution: 'Cubemate teaches you the "why" behind every block so you can explain it in interviews.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
                gradientFrom: 'from-orange-500/20',
                gradientTo: 'to-purple-500/20',
                border: 'border-orange-500/20 hover:border-purple-400/40',
              },
              {
                problem: 'You set up environments for hours before writing one line.',
                solution: 'Cubemate runs 10+ languages instantly. Zero setup. Just code.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                gradientFrom: 'from-yellow-500/20',
                gradientTo: 'to-green-500/20',
                border: 'border-yellow-500/20 hover:border-green-400/40',
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${card.gradientFrom} ${card.gradientTo} border ${card.border} rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:shadow-white/5 hover:-translate-y-1`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="text-gray-500 mb-4">{card.icon}</div>
                <p className="text-gray-500 text-sm mb-4 line-through">{card.problem}</p>
                <p className="text-white text-sm font-medium leading-relaxed">{card.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 lg:py-28">
        <div
          ref={features.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            features.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Level Up
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: 'Execute Code Instantly',
                desc: 'Python, JavaScript, Java, C++, Go, Rust, and more. Write and run code in seconds with zero setup.',
                gradient: 'from-blue-500/20 to-blue-600/5',
                border: 'border-blue-500/20 hover:border-blue-400/40',
                color: 'text-blue-400',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                ),
                title: 'BookMate Tutorials',
                desc: 'AI-generated interactive tutorials. Side-by-side language comparison. Run every example in your browser.',
                gradient: 'from-purple-500/20 to-purple-600/5',
                border: 'border-purple-500/20 hover:border-purple-400/40',
                color: 'text-purple-400',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                ),
                title: 'Workflow & Data',
                desc: 'Data pipelines, web scraping, charts, and automation. Describe what you need, get working output.',
                gradient: 'from-pink-500/20 to-pink-600/5',
                border: 'border-pink-500/20 hover:border-pink-400/40',
                color: 'text-pink-400',
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${card.gradient} border ${card.border} rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:shadow-white/5 hover:-translate-y-1`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={`${card.color} mb-5`}>{card.icon}</div>
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-16 lg:py-20">
        <div
          ref={stats.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            stats.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-3 gap-8">
            {[
              { value: '10+', label: 'Languages' },
              { value: 'Instant', label: 'Execution' },
              { value: 'AI', label: 'Tutorials' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-400 text-sm font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent">
        <div
          ref={useCases.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            useCases.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Built for{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Real Goals
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Learn a New Language',
                desc: 'Pick up Python, Go, or Rust with side-by-side comparisons to languages you already know.',
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                ),
              },
              {
                title: 'Interview Prep',
                desc: 'Practice coding problems with instant execution. Understand solutions, don\'t just memorize them.',
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
              },
              {
                title: 'Build Your Portfolio',
                desc: 'Create real projects with working code. Scrape data, build charts, automate tasks.',
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                ),
              },
              {
                title: 'Understand Legacy Code',
                desc: 'Paste any code. Get explanations, refactoring suggestions, and run it instantly.',
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                ),
              },
            ].map((uc, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 hover:border-blue-400/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-1"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="text-blue-400 mb-4">{uc.icon}</div>
                <h3 className="text-base font-bold text-white mb-2">{uc.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-20 lg:py-28">
        <div
          ref={cta.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${
            cta.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-white/10 rounded-3xl p-12 sm:p-16 overflow-hidden">
            {/* Background glow */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-blue-600/15 rounded-full blur-[100px]" />
            </div>

            <div className="relative">
              <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
                Start free.{' '}
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Upgrade when you're ready.
                </span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Free for 7 days. Pro from $10/month.
              </p>
              <button
                onClick={goLogin}
                className="px-10 py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25"
              >
                Start Learning Free
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-7 h-7 rounded-lg" />
              <span className="text-sm text-gray-400">
                Cubemate. Built for builders.
              </span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <a href="/privacy" className="hover:text-gray-300 transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-gray-300 transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageLATAM;
